import './index.css'

import React, { useCallback } from 'react'
import type { Track } from 'types/track'
import Mixpanel from '@disco/api/mixpanel'
import { TRACK } from '@disco/constants/ItemTypes'
import { MAIN, STEM } from '@disco/constants/Nesting'
import type { ManageNestingFormState } from '@disco/state-management/reducers/forms/manage-nesting'
import { moveItems2 } from '@disco/utils/immutable'
import {
  getStemsFromTracksArray,
  getUpdatedFieldsEntries,
  getVersionsFromTracksArray,
  shouldBeMain,
  unlinkTrackAndShiftPosition,
  updateTrackAndShiftPosition,
} from '@disco/utils/nesting'
import TrackInfo from 'components/common/track-info'
import PaneItemsSkeleton from 'components/explorer/pane/pane-items-skeleton'
import TrackActions from 'components/track-actions'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import CompactAccordion from 'components/ui/compact-accordion'
import DataTable from 'components/ui/data-table'
import Draggable from 'components/ui/draggable'
import DropZonePlaceholder from 'components/ui/drop-zone-placeholder'
import { HoverProvider } from 'components/ui/hover-provider'
import NestingLabelDropdown from 'components/ui/nesting-label-dropdown'
import Sortable from 'components/ui/sortable'
import FileDropWrapper from 'containers/FileDropWrapper'
import TrackDropWrapper from 'containers/TrackDropWrapper'

const Table = ({
  columns,
  tracks,
  loading,
  id,
  updateTrackPosition,
  onClickImport,
}) => {
  const renderItem = useCallback(
    track => {
      if (!track) {
        return null
      }

      if (
        tracks.length === 1 &&
        !id.includes(STEM) &&
        track.nesting_label === MAIN
      ) {
        return (
          <DataTable
            loading={loading}
            appearance="plain"
            columns={columns}
            data={[track]}
          />
        )
      }

      return (
        <Draggable
          key={track.id}
          id={`manage-nesting-track-${track.id}`}
          data={{
            track,
          }}
          type={TRACK}
        >
          <DataTable
            loading={loading}
            appearance="plain"
            columns={columns}
            data={[track]}
          />
        </Draggable>
      )
    },
    [tracks],
  )

  if (loading) {
    return <PaneItemsSkeleton itemsCount={2} />
  }

  return (
    <TrackDropWrapper
      location="manage-nesting"
      onDrop={(data, dropItem) => {
        // don't handle files dropping
        if (!data?.track && !dropItem?.track) {
          return
        }

        const item = data || dropItem
        let sortedItems
        const shiftStems =
          item.track.nesting_label !== STEM && id.includes(STEM)
        const shiftVersions =
          item.track.nesting_label === STEM && !id.includes(STEM)
        let nesting_label = shiftStems ? STEM : item.track.nesting_label

        if (shiftVersions) {
          nesting_label = null
        }

        if (!shiftStems && !shiftVersions && data) {
          sortedItems = moveItems2(tracks, data.sortableKey, data.index)
        }

        updateTrackPosition({
          track: item.track,
          newFields: {
            // If no position is provided, drop at the end of the list
            nesting_position:
              item.index !== undefined ? item.index : tracks.length,
            nesting_label,
          },
          sortedItems,
          shiftStems,
          shiftVersions,
        })
      }}
    >
      <Sortable
        keyExtractor={(item: Track, index: number): string =>
          `${item.id}-${index}`
        }
        accept={[TRACK]}
        id={id}
        items={tracks}
        renderItem={renderItem}
      />
      <DropZonePlaceholder small onClick={onClickImport} />
    </TrackDropWrapper>
  )
}

type ManageNestingTableProps = {
  tracksLoading: boolean
  formState: ManageNestingFormState
  nestedTracks: Track[]
  updateForm: (...args: Array<any>) => any
  onFilesDrop: (...args: Array<any>) => any
  onClickImport: (isStem?: boolean) => void
}

const ManageNestingTable = ({
  tracksLoading,
  formState,
  nestedTracks,
  updateForm,
  onFilesDrop,
  onClickImport,
}: ManageNestingTableProps): React.ReactNode => {
  let versions = getVersionsFromTracksArray(nestedTracks)
  let stems = getStemsFromTracksArray(nestedTracks)

  const unlinkItem = track => {
    const update = unlinkTrackAndShiftPosition({
      track,
      tracks: nestedTracks,
    })
    const newParent = getUpdatedFieldsEntries(
      update.itemsWithUpdatedFields,
    ).find(([, value]) => shouldBeMain(value))

    if (newParent) {
      update.mainTrackId = newParent[0]
    }

    updateForm(update, {
      type: 'REMOVE_TRACK',
      trackId: track.id,
      nestingLabel: track.nesting_label,
    })
  }

  const updateLabel = ({ track, label, shiftStems, shiftVersions }) => {
    if (!formState.mainTrackId) return
    let newPos = track.nesting_position

    if (shiftVersions) {
      newPos = versions.length
    }

    if (shiftStems) {
      newPos = stems.length
    }

    if (label === MAIN) {
      newPos = 0
    }

    const update: Record<string, any> = updateTrackAndShiftPosition({
      tracks: nestedTracks,
      track,
      newFields: {
        nesting_label: label,
        nesting_parent_id: label !== MAIN ? formState.mainTrackId : null,
        nesting_position: newPos,
      },
      shiftStems,
      shiftVersions,
    })

    if (label === MAIN) {
      update.mainTrackId = track.id
    }

    updateForm(update, {
      type: track.nesting_label === null ? 'ADD_LABEL' : 'CHANGE_LABEL',
      trackId: track.id,
      nestingLabel: label,
    })
  }

  const updateTrackPosition = ({
    track,
    newFields,
    sortedItems,
    shiftStems,
    shiftVersions,
  }) => {
    if (!formState.mainTrackId) return
    const willBeMain = shouldBeMain(newFields)
    const update: Record<string, any> = updateTrackAndShiftPosition({
      tracks: nestedTracks,
      track,
      newFields: {
        ...newFields,
        nesting_parent_id: !willBeMain ? formState.mainTrackId : null,
        nesting_label: !willBeMain ? newFields.nesting_label : MAIN,
      },
      sortedItems,
      shiftStems,
      shiftVersions,
    })
    const newParent = getUpdatedFieldsEntries(
      update.itemsWithUpdatedFields,
    ).find(([, value]) => shouldBeMain(value))

    if (willBeMain) {
      update.mainTrackId = track.id
    } else if (newParent) {
      update.mainTrackId = newParent[0]
    }

    updateForm(update)
  }

  const showFileSelect = versions.length + stems.length <= 1 && !tracksLoading

  const _onTrackMenuOpen = trackId => {
    // Event #7 from https://disco-team.atlassian.net/browse/DISCO-4871
    Mixpanel.sendPureEvent('Open menu', {
      Modal: 'Manage nesting',
      Context: 'Nesting',
      Menu: 'Track',
      ['Track ID']: trackId,
    })
  }

  if (!tracksLoading && nestedTracks?.length === 0) {
    return (
      <>
        <Alert status="warning" icon="warning">
          This track does not contain versions
        </Alert>
        {showFileSelect && <DropZonePlaceholder onClick={onClickImport} />}
      </>
    )
  }

  const columns = [
    {
      render: track => {
        return (
          <HoverProvider>
            <TrackInfo location="explorer" track={track} />
          </HoverProvider>
        )
      },
    },
    {
      width: 'content',
      contentType: 'button',
      render: track => {
        return (
          <NestingLabelDropdown
            onChange={label => {
              if (track.nesting_label === label) {
                return
              }

              updateLabel({
                track,
                label,
                shiftStems: track.nesting_label !== STEM && label === STEM,
                shiftVersions:
                  (track.nesting_label === STEM && label !== STEM) ||
                  label === MAIN,
              })
            }}
            label={track.nesting_label}
          />
        )
      },
    },
    {
      contentType: 'button',
      width: 'content',
      render: track => {
        if (versions.length === 1 && track.nesting_label === MAIN) {
          return <Button appearance="icon" disabled />
        }

        return (
          <Button
            appearance="icon"
            icon="clear"
            onClick={() => {
              unlinkItem(track)
            }}
          />
        )
      },
    },
    {
      contentType: 'button',
      width: 'content',
      render: track => (
        <TrackActions
          buttonSize="compact"
          track={track}
          addToCustomGroup
          addToNewCustomGroup
          allowSharing
          deleteTrack={true}
          downloadTrack
          editMetadata
          manageTrackFormats
          permissionsAndSharing
          copyMetadata
          hideEls={['native_tags', 'share_shortcut', 'notes_shortcut']}
          onMenuOpen={() => _onTrackMenuOpen(track.id)}
        />
      ),
    },
  ]
  return (
    <>
      <FileDropWrapper
        multiple
        onDrop={files =>
          onFilesDrop({
            files,
          })
        }
      >
        <CompactAccordion title="VERSIONS" arrowPosition="left" open>
          <Table
            loading={tracksLoading}
            columns={columns}
            tracks={versions}
            id="nesting-modal-versions"
            updateTrackPosition={updateTrackPosition}
            onClickImport={() => onClickImport(false)}
          />
        </CompactAccordion>
      </FileDropWrapper>

      <FileDropWrapper
        multiple
        onDrop={files =>
          onFilesDrop({
            files,
            isStem: true,
          })
        }
      >
        <CompactAccordion title="STEMS" arrowPosition="left" open>
          <Table
            loading={tracksLoading}
            columns={columns}
            tracks={stems}
            id={`nesting-modal-${STEM}`}
            updateTrackPosition={updateTrackPosition}
            onClickImport={() => onClickImport(true)}
          />
        </CompactAccordion>
      </FileDropWrapper>
    </>
  )
}

export default ManageNestingTable
